import { EMPTY_TILE, unpackTile } from "../document/packed-tile.ts";
import type { MapDocument, TileRef, WorldDocument } from "../document/types.ts";
import type { ValidationIssue } from "./adapter.ts";
import type { EditorCatalogue } from "./catalogue.ts";
import { packOf } from "./packs.ts";

/**
 * The Issue for a placed draft (ADR-107 decision 31). A sprite of a studio pack that is not published may be placed
 * by any builder, and a world that places one does not publish until its pack does. The editor's catalogue marks
 * such props `draft`; each one the map in view places is a warning here, by map, name and pack, carried by the
 * first prop that places it so a click selects that prop. Nothing here reads a studio document: the catalogue the
 * last compile wrote is what the editor knows.
 */

/**
 * The kind of the Issue, which an ignore binds to.
 */
export const DRAFT_KIND = "studio.draft";

/**
 * The drafts a map places, each as one warning.
 *
 * @param map The map in view.
 * @param catalogue The editor's catalogue, or undefined with none.
 * @returns One warning a draft frame a prop of the map shows or its behaviour names, in the layers' and the props'
 *   order, each frame once.
 */
export function draftPlacementIssues(map: MapDocument, catalogue: EditorCatalogue | undefined): ValidationIssue[] {
  const drafts = new Map((catalogue?.props ?? []).filter((prop) => prop.draft === true).map((prop) => [`${prop.tileset}\u0000${prop.tile}`, prop]));
  if (drafts.size === 0) {
    return [];
  }
  const packNames = new Map((catalogue?.packs ?? []).map((pack) => [pack.id, pack.name]));
  const seen = new Set<string>();
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const frames: TileRef[] = [prop.frame, ...Object.values(prop.behaviour?.frames ?? {}), ...Object.values(prop.behaviour?.animations ?? {})];
      for (const frame of frames) {
        const key = `${frame.tileset}\u0000${frame.tile}`;
        const draft = drafts.get(key);
        if (draft === undefined || seen.has(key)) {
          continue;
        }
        seen.add(key);
        const pack = packOf(frame.tileset) ?? frame.tileset;
        const packName = packNames.get(pack) ?? pack;
        issues.push({ mapId: map.id, layerId: layer.id, propId: prop.id, kind: DRAFT_KIND, severity: "warning", frame: { tileset: frame.tileset, tile: frame.tile }, message: `${map.id}: ${draft.name} (${packName}, draft). This world does not publish until ${packName} is published` });
      }
    }
  }
  return issues;
}

/**
 * The kind of the refusal of a studio sprite painted as a tile.
 */
export const STUDIO_TILE_KIND = "studio.tile";

const STUDIO_SUFFIX = ":studio";

/**
 * The tile layers of a map that hold a cell of a studio tileset, each as one refusal. A studio sprite is a prop: a
 * publish reads the props a world places to refuse a draft (decision 29), and a sprite painted as a tile would be
 * drawn without being a placement, so no document may hold one. A world that lists no studio tileset is not read.
 *
 * @param world The document, for its tileset list.
 * @param map The map.
 * @returns One refusal a tile layer and studio tileset, in the layers' order.
 */
export function studioTileIssues(world: WorldDocument, map: MapDocument): ValidationIssue[] {
  const studio = new Map<number, string>();
  world.tilesets.forEach((tileset, index) => {
    if (tileset.id.endsWith(STUDIO_SUFFIX)) {
      studio.set(index, tileset.id);
    }
  });
  if (studio.size === 0) {
    return [];
  }
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind !== "tile") {
      continue;
    }
    const found = new Set<string>();
    for (const packed of layer.tiles) {
      const tileset = packed === EMPTY_TILE ? undefined : studio.get(unpackTile(packed).tilesetIndex);
      if (tileset !== undefined && !found.has(tileset)) {
        found.add(tileset);
        issues.push({ mapId: map.id, layerId: layer.id, kind: STUDIO_TILE_KIND, message: `layer ${layer.id} holds a tile of ${tileset}; a studio sprite is placed as a prop and is never a tile` });
      }
    }
  }
  return issues;
}
